import { useTranslation } from 'react-i18next';
import { generatePath, Link, useParams } from 'react-router-dom';

import { ParkingSpotReadModel, SpaceDiscriminator } from '@client/api';
import { Card, CardContent, CardHeader, CardTitle, DD, DT, GridCol, GridRow, isDefinedNumber, isDefinedString, toNumberString } from '@client/toolkit';

import { PortfolioPaths } from '../../../../portfolio.router';

export interface ParkingSpotInformationCardProps {
  className?: string;
  space?: ParkingSpotReadModel;
  isFetching?: boolean;
}

export function ParkingSpotInformationCard({ className, space, isFetching }: ParkingSpotInformationCardProps) {
  const { buildingId } = useParams<{ buildingId: string, spaceId: string, discriminator: SpaceDiscriminator }>();

  const { t, ready } = useTranslation('rentalObjects');
  const { t: tType } = useTranslation('type');
  const { t: tGlobal } = useTranslation('global');

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense" loading={!ready}>
          {t('parkingSpot.parkingSpotInformation')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="pb-2">
          <DD
            loading={isFetching}
            className="mb-1.5"
            variant="section"
            isEmpty={!isDefinedString(space?.name)}
          >
            {space?.name}
          </DD>
        </div>

        <div className="space-y-2">
          <div className="flex flex-col py-2">
            <GridRow cols={2} spacingY={1} spacingX={2}>
              <GridCol span={1}>
                <DT className="w-1/2">{tGlobal('parkingSpot.type')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD loading={isFetching} isEmpty={!space?.type}>
                  {tType(`ParkingSpotType.${space?.type}`)}
                </DD>
              </GridCol>

              <GridCol span={1}>
                <DT className="w-1/2">{tGlobal('parkingSpot.area')}</DT>
              </GridCol>
              <GridCol span={1}>
                <DD loading={isFetching} isEmpty={!isDefinedNumber(space?.area?.metric)}>
                  {toNumberString(space?.area?.metric ?? undefined, undefined, {
                    unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                  })}
                </DD>
              </GridCol>

              {space?.parent?.discriminator !== 'Building' &&
                <>
                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('space.parent')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!space?.parent?.name}>
                      <Link className="hover:underline" to={
                        space?.parent != null ?
                          generatePath(PortfolioPaths.Space, { buildingId, spaceId: space.parent.id, discriminator: space.parent.discriminator })
                          :
                          ''
                      }
                        replace
                      >
                        {space?.parent?.name}
                      </Link>
                    </DD>
                  </GridCol>
                </>
              }
            </GridRow>
          </div>

          <div className="flex flex-col py-2">
            <DT className="mb-2">{tGlobal('parkingSpot.description')}</DT>
            <DD
              loading={isFetching}
              isEmpty={!isDefinedString(space?.description)}
              className="text-sm text-white/60"
              variantFontSize="none"
            >
              {space?.description}
            </DD>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
